// @ts-nocheck
import PSPDFKitWeb from 'pspdfkit';
import { uniq, get } from 'lodash';
import { generateSequentialUUID, notEmpty } from 'utils/js';

type Rect = {
  pageIndex: number;
  rect: PSPDFKitWeb.Geometry.Rect;
};

type Rects = {
  pageIndex: number;
  rects: PSPDFKitWeb.Immutable.List;
};

type CustomData = {
  annotationId?: string;
  text?: string;
  type: string;
  decorations: string[];
  name?: string;
  isLink?: boolean;
};

// The pageIndex for rects in annotations is always the same
// (since an annotation can only be on one page)
// We're only adding this to maintain type consistency with TextSelection
// whose rects CAN be over more than one page
function appendPageIndex(pageIndex) {
  return ([, rect]) => {
    return {
      pageIndex,
      rect,
    };
  };
}

export async function getSelectionRects(textSelection) {
  // TODO: Check what happens if the selection is across multiple pages
  const all = await textSelection?.getSelectedRectsPerPage();
  const rects = all?.map(({ rects }) => {
    return rects;
  });

  return PSPDFKitWeb.Immutable.List(rects);
}

export class Selection {
  entity:
    | PSPDFKitWeb.Annotations.HighlightAnnotation
    | PSPDFKitWeb.TextSelection;
  instance: PSPDFKitWeb.Instance;

  constructor(
    entity:
      | PSPDFKitWeb.Annotations.HighlightAnnotation
      | PSPDFKitWeb.TextSelection,
    instance: PSPDFKitWeb.Instance,
  ) {
    this.entity = entity;
    this.instance = instance;
  }

  isEmpty(customData: CustomData): boolean {
    return (
      !customData?.decorations?.length && !customData.name && !customData.isLink
    );
  }

  isTextSelection(): boolean {
    return this.entity instanceof PSPDFKitWeb.TextSelection;
  }

  isAnnotation(): boolean {
    return this.entity instanceof PSPDFKitWeb.Annotations.HighlightAnnotation;
  }

  getID(): string | undefined {
    return this.entity.id || undefined;
  }

  getText(): Promise<string> {
    if (this.isAnnotation()) {
      return this.instance.getMarkupAnnotationText(this.entity);
    } else {
      return this.entity.getText();
    }
  }

  async getRect(): Promise<Rect> {
    if (this.isAnnotation()) {
      return {
        rect: this.entity.boundingBox,
        pageIndex: this.entity.pageIndex,
      };
    } else {
      return {
        rect: PSPDFKitWeb.Geometry.Rect.union(
          (await getSelectionRects(this.entity)).first(),
        ),
        pageIndex: this.entity.startPageIndex,
      };
    }
  }

  async getRects(): Promise<Rects> {
    if (this.isAnnotation()) {
      const rects = Object.entries(this.entity.rects).map(
        appendPageIndex(this.entity.pageIndex),
      );

      return {
        rects,
        pageIndex: this.entity.pageIndex,
      };
    } else {
      return {
        rects: (await getSelectionRects(this.entity)).first(),
        pageIndex: this.entity.startPageIndex,
      };
    }
  }

  getCustomData(key?: string): CustomData | any {
    return key ? get(this.entity.customData, key) : this.entity.customData;
  }

  async toggleDecoration(decoration: string): Promise<this> {
    const decorations = this.getCustomData('decorations') ?? [];

    if (decorations?.includes(decoration)) {
      const newDecorations = decorations.filter(
        (value) => value !== decoration,
      );
      return this.saveAnnotation({ decorations: newDecorations });
    } else {
      const newDecorations = uniq([...decorations, decoration]);
      return this.saveAnnotation({ decorations: newDecorations });
    }
  }

  hasDecorations() {
    return notEmpty(this.getCustomData('decorations'));
  }

  shouldDeleteAnnotation() {
    return this.isAnnotation() && this.isEmpty(this.getCustomData());
  }

  async saveAnnotation(customData): Promise<this> {
    if (this.isAnnotation()) {
      return this.updateAnnotation(customData);
    } else {
      return this.createAnnotation(customData);
    }
  }

  async updateAnnotation(customData): Promise<this> {
    const updated = this.entity.set('customData', {
      ...this.getCustomData(),
      ...customData,
    });

    const [updatedAnnotation] = await this.instance.update(updated);

    this.entity = updatedAnnotation;

    return this;
  }

  async createAnnotation(customData): Promise<this> {
    const boundingBox = await this.getRect();
    const rects = await this.getRects();

    customData.annotationId = generateSequentialUUID();
    customData.text = await this.getText();
    customData.type = 'custom';

    const annotation = {
      pageIndex: boundingBox.pageIndex,
      rects: rects.rects,
      boundingBox: boundingBox.rect,
      customData,
      color: PSPDFKitWeb.Color.BLUE, // This color is never actually shown, but is required by the HighlightAnnotation type
    };
    const [createdAnnotation] = await this.instance.create(
      new PSPDFKitWeb.Annotations.HighlightAnnotation(annotation),
    );

    this.entity = createdAnnotation;

    return this;
  }

  deleteAnnotation() {
    try {
      if (this.isAnnotation()) {
        return this.instance.delete(this.entity.id);
      }
    } catch (error) {
      console.log('------------ deleteAnnotation ------------- ');
      console.log(error);
    }
  }

  isLinkAnnotationWithoutName() {
    return (
      this.isAnnotation() &&
      this.getCustomData('isLink') &&
      !this.getCustomData('name')
    );
  }
}
